import React, { useState, useEffect, useRef, useContext } from "react";
import { parseISO, format } from "date-fns";
import * as Yup from "yup";
import { Formik, FieldArray, Form, Field } from "formik";
import { toast } from "react-toastify";
import { AuthContext } from "../../context/Auth/AuthContext";

import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import Typography from "@material-ui/core/Typography";
import IconButton from "@material-ui/core/IconButton";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import CircularProgress from "@material-ui/core/CircularProgress";
import Switch from "@material-ui/core/Switch";


import Grid from '@mui/material/Grid';
import Autocomplete from '@mui/material/Autocomplete';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Box from '@mui/material/Box';

import { i18n } from "../../translate/i18n";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { sanitizeContactDynamicFields } from "../../utils/sanitizeContactDynamicFields";
import ContactFieldAutocomplete from "../ContactFieldAutocomplete";
import { TagsContainer } from "../TagsContainer";
import useQueues from "../../hooks/useQueues";
import { createFilterOptions } from '@mui/material/Autocomplete';
// import AsyncSelect from "../AsyncSelect";

const SITUACAO_FALLBACK = ["ENGAJADO", "NEUTRO", "ESPECULANDO"];
const CANAL_FALLBACK = [
	"1 Rls ORG", "2 Rls PAT", "3 CRI CAMP", "4 CRI TURB", "5 YouTube", "6 TikTok",
	"7 Linkedin", "8 Site", "9 E-commerce", "10 LP", "11 IND CLI", "12 DISP WTS",
	"13 ML", "14 IND TEC", "15 IND FOR", "16 E-mail MKT", "17 OUT CONS", "18 SDRS", "19 REPRE",
];
const REGIAO_FALLBACK = ["NORTE", "NORDESTE", "CENTRO OESTE", "SUL", "SUDESTE", "SP"];

const useStyles = makeStyles(theme => ({
	root: {
		display: "flex",
		flexWrap: "wrap",
	},
	textField: {
		marginRight: theme.spacing(1),
		flex: 1,
	},

	extraAttr: {
		display: "flex",
		justifyContent: "center",
		alignItems: "center",
	},

	btnWrapper: {
		position: "relative",
	},

	buttonProgress: {
		color: green[500],
		position: "absolute",
		top: "50%",
		left: "50%",
		marginTop: -12,
		marginLeft: -12,
	},


}));

const formatDateForInput = (date) => {
	if (!date) return '';
	
	// Se já está no formato YYYY-MM-DD, retorna como está
	if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
		return date;
	}
	
	// Se é uma data ISO ou objeto Date, converte para YYYY-MM-DD
	const d = new Date(date);
	if (isNaN(d.getTime())) return '';
	
	// Usar métodos locais para evitar problemas de timezone
	const year = d.getFullYear();
	const month = String(d.getMonth() + 1).padStart(2, '0');
	const day = String(d.getDate()).padStart(2, '0');
	
	return `${year}-${month}-${day}`;
};
  
const parseDateFromInput = (dateString) => {
	if (!dateString) return null;
	
	// Se já está no formato YYYY-MM-DD, retorna como está
	if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
		return dateString;
	}
	
	// Se é uma data ISO, extrai apenas a parte da data
	if (dateString.includes('T')) {
		return dateString.split('T')[0];
	}
	
	return dateString;
};

const ContactSchema = Yup.object().shape({
	name: Yup.string()
		.min(2, "Too Short!")
		.max(250, "Too Long!")
		.required("Required"),
	number: Yup.string().min(8, "Too Short!").max(50, "Too Long!"),
	email: Yup.string().email("Invalid email"),
	customDynamicFields: Yup.mixed().nullable(),
});

const ContactModal = ({ open, onClose, contactId, initialValues, onSave, initialTab = 0 }) => {
	const classes = useStyles();
	const isMounted = useRef(true);
	const { user } = useContext(AuthContext);

	const initialState = {
		name: "",
		number: "",
		email: "",
		disableBot: false,
		lgpdAcceptedAt: "",
		birthDate: "",
		situacao: "",
		canal: "",
		regiao: "",
		campanha: "",
		produto: "",
		customDynamicFields: {},
	};

	const [contact, setContact] = useState(initialState);
	const [disableBot, setDisableBot] = useState(false);
	const [selectedUser, setSelectedUser] = useState(null);
	const [selectedQueue, setSelectedQueue] = useState(null);
	const [queues, setQueues] = useState([]);
	const [allQueues, setAllQueues] = useState([]);
	const [options, setOptions] = useState([]);
	const [loading, setLoading] = useState(false);
	const [searchParam, setSearchParam] = useState("");
	const [activeFloup, setActiveFloup] = useState(null);
	const [loadingFloup, setLoadingFloup] = useState(false);
	const [autocompleteOpen, setAutocompleteOpen] = useState(false);
	const [selectedTags, setSelectedTags] = useState([]);
	const [tabValue, setTabValue] = useState(0);
	const [situacaoOptions, setSituacaoOptions] = useState([]);
	const [canalOptions, setCanalOptions] = useState([]);
	const [regiaoOptions, setRegiaoOptions] = useState([]);
	const [contactFieldGroups, setContactFieldGroups] = useState([]);
	const [customAttributeRows, setCustomAttributeRows] = useState([]);
	const [loadingCustomAttributes, setLoadingCustomAttributes] = useState(false);

	const { findAll: findAllQueues } = useQueues();

	// Configurar filterOptions para busca no servidor
	const filterOptions = createFilterOptions({
		trim: true,
	});

	useEffect(() => {
		return () => {
			isMounted.current = false;
		};
	}, []);

	/** Abas: 0 = dados principais, 1 = dados adicionais (ex.: abrir direto do Kanban). */
	useEffect(() => {
		if (!open) return;
		const tab = initialTab === 1 ? 1 : 0;
		setTabValue(tab);
	}, [open, initialTab]);

	// Carregar todas as filas quando o modal abrir
	useEffect(() => {
		if (!open) return;
		
		const loadAllQueues = async () => {
			try {
				const list = await findAllQueues();
				if (isMounted.current) {
					setAllQueues(list);
					// Se não há usuário selecionado, mostrar todas as filas
					if (!selectedUser) {
						setQueues(list);
					}
				}
			} catch (err) {
				if (isMounted.current) {
					toastError(err);
				}
			}
		};

		loadAllQueues();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open]);

	// Carregar opções dos campos select dinamicamente (por empresa)
	useEffect(() => {
		if (!open || !user?.companyId) return;

		const loadCustomAttributes = async () => {
			setLoadingCustomAttributes(true);
			try {
				let groups = [];
				try {
					const groupsRes = await api.get("/contact-field-groups");
					groups = Array.isArray(groupsRes.data) ? groupsRes.data : [];
				} catch (groupsErr) {
					console.warn("[ContactModal] contact-field-groups indisponível, usando abas legadas:", groupsErr);
					groups = [
						{
							id: -1,
							param: "SITUACAO",
							label: i18n.t("customContactFieldsPage.tabSituacao"),
							fieldKind: "SELECT_SINGLE",
							multiMax: null,
							sortOrder: 0,
						},
						{
							id: -2,
							param: "CANAL",
							label: i18n.t("customContactFieldsPage.tabCanal"),
							fieldKind: "SELECT_SINGLE",
							multiMax: null,
							sortOrder: 1,
						},
						{
							id: -3,
							param: "REGIAO",
							label: i18n.t("customContactFieldsPage.tabRegiao"),
							fieldKind: "SELECT_SINGLE",
							multiMax: null,
							sortOrder: 2,
						},
					];
				}

				const [manageRes, situacaoResponse, canalResponse, regiaoResponse] = await Promise.all([
					api.get("/custom-attributes/manage"),
					api.get("/custom-attributes", {
						params: { context: "CONTACT", param: "SITUACAO" },
					}),
					api.get("/custom-attributes", {
						params: { context: "CONTACT", param: "CANAL" },
					}),
					api.get("/custom-attributes", {
						params: { context: "CONTACT", param: "REGIAO" },
					}),
				]);

				if (isMounted.current) {
					setContactFieldGroups(groups);
					setCustomAttributeRows(Array.isArray(manageRes.data) ? manageRes.data : []);
					setSituacaoOptions(situacaoResponse.data || []);
					setCanalOptions(canalResponse.data || []);
					setRegiaoOptions(regiaoResponse.data || []);
				}
			} catch (err) {
				console.error("[ContactModal] Erro ao buscar atributos customizados:", err);
				if (isMounted.current) {
					toastError(err);
					setContactFieldGroups([]);
					setCustomAttributeRows([]);
					setSituacaoOptions([]);
					setCanalOptions([]);
					setRegiaoOptions([]);
				}
			} finally {
				if (isMounted.current) {
					setLoadingCustomAttributes(false);
				}
			}
		};

		loadCustomAttributes();
	}, [open, user?.companyId]);

	useEffect(() => {
		if (loadingCustomAttributes || !contactFieldGroups.length) return;
		setContact(prev => {
			if (!prev?.customDynamicFields) return prev;
			const sanitized = sanitizeContactDynamicFields(
				prev.customDynamicFields,
				contactFieldGroups,
				customAttributeRows
			);
			if (
				JSON.stringify(sanitized) === JSON.stringify(prev.customDynamicFields)
			) {
				return prev;
			}
			return { ...prev, customDynamicFields: sanitized };
		});
	}, [
		contactFieldGroups,
		customAttributeRows,
		loadingCustomAttributes,
	]);

	// Buscar usuários quando o searchParam mudar
	useEffect(() => {
		console.log("[ContactModal] useEffect searchParam:", { searchParam, open, length: searchParam?.length });
		
		if (!open) {
			setOptions([]);
			setLoading(false);
			setSearchParam("");
			return;
		}
		
		if (!searchParam || searchParam.length < 3) {
			setOptions([]);
			setLoading(false);
			return;
		}
		
		const delayDebounceFn = setTimeout(() => {
			console.log("[ContactModal] Buscando usuários com searchParam:", searchParam);
			setLoading(true);
			const fetchUsers = async () => {
				try {
					const { data } = await api.get("/users/", {
						params: { searchParam, showAll: true },
					});
					if (isMounted.current) {
						console.log("[ContactModal] Usuários encontrados:", data.users?.length || 0, data.users);
						const users = data.users || [];
						setOptions(users);
						setLoading(false);
						console.log("[ContactModal] Options atualizadas - total:", users.length);
						// Abrir dropdown se houver opções e o campo ainda estiver focado
						if (users.length > 0 && searchParam.length >= 3) {
							setTimeout(() => {
								if (isMounted.current) {
									setAutocompleteOpen(true);
									console.log("[ContactModal] Forçando abertura do dropdown");
								}
							}, 100);
						}
					}
				} catch (err) {
					console.error("[ContactModal] Erro ao buscar usuários:", err);
					if (isMounted.current) {
						setLoading(false);
						toastError(err);
					}
				}
			};

			fetchUsers();
		}, 500);
		return () => clearTimeout(delayDebounceFn);
	}, [searchParam, open]);

	useEffect(() => {
		const fetchContact = async () => {
		  if (initialValues) {
			setContact(prevState => {
			  return { 
				...prevState, 
				...initialValues,
				// Formatar a data corretamente
				birthDate: formatDateForInput(initialValues.birthDate),
				situacao: initialValues.situacao || "",
				canal: initialValues.canal || "",
				regiao: initialValues.regiao || "",
				campanha: initialValues.campanha || "",
				produto: initialValues.produto || "",
				customDynamicFields:
					initialValues.customDynamicFields &&
					typeof initialValues.customDynamicFields === "object"
						? initialValues.customDynamicFields
						: {},
			  };
			});
		  }

			if (!contactId) {
				setActiveFloup(null);
				return;
			}

			try {
				const { data } = await api.get(`/contacts/${contactId}`);
				if (isMounted.current) {
					setContact({
						...data,
						birthDate: formatDateForInput(data.birthDate),
						situacao: data.situacao || "",
						canal: data.canal || "",
						regiao: data.regiao || "",
						campanha: data.campanha || "",
						produto: data.produto || "",
						customDynamicFields:
							data.customDynamicFields && typeof data.customDynamicFields === "object"
								? data.customDynamicFields
								: {},
				});
					setDisableBot(data.disableBot)

					// Preenche automaticamente os campos de Wallet e Queue
					if (data.contactWallets && data.contactWallets.length > 0) {
						const wallet = data.contactWallets[0].wallet;
						const queue = data.contactWallets[0].queue;

						// Buscar as filas do usuário para permitir mudança
						const fetchUserQueues = async () => {
							try {
								// Buscar usuário específico para obter suas filas
								const { data: userData } = await api.get(`/users/${wallet.id}`);
								if (userData && Array.isArray(userData.queues) && userData.queues.length > 0) {
									if (isMounted.current) {
										setQueues(userData.queues);
										console.log("[ContactModal] Filas carregadas do usuário:", userData.queues.length);
									}
								} else {
									// Tentar buscar pela lista de usuários
									const { data: listData } = await api.get("/users/", {
										params: { searchParam: wallet.name, showAll: true },
									});
									const user = listData.users?.find(u => u.id === wallet.id);
									if (user && Array.isArray(user.queues) && user.queues.length > 0) {
										if (isMounted.current) {
											setQueues(user.queues);
											console.log("[ContactModal] Filas carregadas da lista:", user.queues.length);
										}
									} else {
										// Se não encontrar filas do usuário, usar apenas a fila atual
										if (isMounted.current) {
											setQueues([{ id: queue.id, name: queue.name }]);
										}
									}
								}
							} catch (err) {
								console.error("[ContactModal] Erro ao buscar filas do usuário:", err);
								// Em caso de erro, usar apenas a fila atual
								if (isMounted.current) {
									setQueues([{ id: queue.id, name: queue.name }]);
								}
							}
						};

						const userObj = {
							id: wallet.id,
							name: wallet.name,
						};
						
						setSelectedUser(userObj);
						setSelectedQueue(queue.id);
						// ✅ IMPORTANTE: Sincronizar searchParam com o nome do usuário selecionado
						setSearchParam(wallet.name || "");

						// Buscar filas do usuário
						fetchUserQueues();
					} else {
						// Limpa os valores quando não há contactWallets
						setSelectedUser(null);
						setSelectedQueue(null);
						// Mostrar todas as filas quando não há usuário selecionado
						if (allQueues.length > 0) {
							setQueues(allQueues);
						}
					}
				}

				// Buscar Floup ativo do contato
				setLoadingFloup(true);
				try {
					const floupResponse = await api.get(`/plugins/floup/contact/${contactId}/active`);
					if (isMounted.current) {
						setActiveFloup(floupResponse.data);
					}
				} catch (floupErr) {
					// Se não encontrar Floup ativo, não é erro, apenas não há Floup
					if (isMounted.current) {
						setActiveFloup(null);
					}
				} finally {
					if (isMounted.current) {
						setLoadingFloup(false);
					}
				}
			} catch (err) {
				toastError(err);
			}
		};

		fetchContact();
	}, [contactId, open, initialValues]);

	const handleClose = () => {
		onClose();
		setContact(initialState);
		setSelectedUser(null);
		setSelectedQueue(null);
		setQueues([]);
		setAllQueues([]);
		setSearchParam("");
		setOptions([]);
		setActiveFloup(null);
		setLoading(false);
		setAutocompleteOpen(false);
		setSelectedTags([]);
		setTabValue(0);
	};

	const handleSaveContact = async values => {
		try {
		  const contactData = {
			...values,
			disableBot: disableBot,
			birthDate: parseDateFromInput(values.birthDate),
			customDynamicFields: sanitizeContactDynamicFields(
				values.customDynamicFields,
				contactFieldGroups,
				customAttributeRows
			),
		  };
	  
		  if (contactId) {
			if (!selectedUser && !selectedQueue) {
			  delete contact.contactWallets;
			  await api.delete(`/contacts/wallet/${contactId}`);
			}
	  
			const { contactWallets, ...valuesWithoutWallets } = contactData;
			delete contact.contactWallets;
	  
			await api.put(`/contacts/${contactId}`, contactData);
	  
			if (selectedUser && selectedQueue && selectedUser !== null && selectedQueue !== null) {
			  await api.put(`/contacts/wallet/${contactId}`, {
				wallets: {
				  userId: selectedUser.id,
				  queueId: selectedQueue,
				},
			  });
			}
	  
			handleClose();
	  
		  } else {
			delete contactData.contactWallets;
	  
			const { data } = await api.post("/contacts", contactData);
	  
			// Sincronizar tags se houver tags selecionadas
			if (data.id && selectedTags && selectedTags.length > 0) {
				try {
					await api.post(`/tags/sync`, {
						contactId: data.id,
						tags: selectedTags
					});
				} catch (err) {
					console.error('[ContactModal] Erro ao sincronizar tags:', err);
					// Não bloquear o salvamento se houver erro nas tags
				}
			}
	  
			if (data.id && selectedUser && selectedQueue) {
			  await api.put(`/contacts/wallet/${data.id}`, {
				wallets: {
				  userId: selectedUser.id,
				  queueId: selectedQueue,
				},
			  });
			}
	  
			if (onSave) {
			  onSave(data);
			}
	  
			handleClose();
		  }
		  toast.success(i18n.t("contactModal.success"));
		} catch (err) {
		  toastError(err);
		}
	  };

	return (
		<div className={classes.root}>
			<Dialog 
				open={open} 
				onClose={handleClose} 
				maxWidth="lg" 
				scroll="paper"
				disableEnforceFocus={true}
				disableAutoFocus={true}
				disableRestoreFocus={true}
				PaperProps={{
					style: {
						zIndex: 10003,
						position: 'fixed'
					}
				}}
				BackdropProps={{
					style: {
						zIndex: 10002,
						backgroundColor: 'rgba(0, 0, 0, 0.5)',
						position: 'fixed'
					}
				}}
				style={{
					zIndex: 10003,
					position: 'fixed'
				}}
			>
				<DialogTitle id="form-dialog-title">
					{contactId
						? `${i18n.t("contactModal.title.edit")}`
						: `${i18n.t("contactModal.title.add")}`}
				</DialogTitle>
				<Formik
					initialValues={contact}
					enableReinitialize={true}
					validationSchema={ContactSchema}
					onSubmit={(values, actions) => {
						setTimeout(() => {
							handleSaveContact(values);
							actions.setSubmitting(false);
						}, 400);
					}}
				>
					{({ values, errors, touched, isSubmitting, setFieldValue }) => (
						<Form>
							<DialogContent dividers>
								<Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
									<Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
										<Tab label={i18n.t("contactModal.form.mainInfo")} />
										<Tab label={i18n.t("contactModal.form.aditionalInfo")} />
									</Tabs>
								</Box>

								{tabValue === 0 && (
									<Box>
								<Field
									as={TextField}
									label={i18n.t("contactModal.form.name")}
									name="name"
									autoFocus
									error={touched.name && Boolean(errors.name)}
									helperText={touched.name && errors.name}
									variant="outlined"
									margin="dense"
									className={classes.textField}
								/>
								<Grid container spacing={2}>
									<Grid item xs={12} sm={6}>
										<Field
											as={TextField}
											label={i18n.t("contactModal.form.number")}
											name="number"
											error={touched.number && Boolean(errors.number)}
											helperText={touched.number && errors.number}
											placeholder="5513912344321"
											variant="outlined"
											margin="dense"
											fullWidth
										/>
									</Grid>
									<Grid item xs={12} sm={6}>
										<TextField
											label="LID (Linked Device ID)"
											value={contact.lid || ""}
											disabled
											fullWidth
											margin="dense"
											variant="outlined"
											helperText="Identificador do dispositivo vinculado"
										/>
									</Grid>
								</Grid>
								<div>
									<Field
										as={TextField}
										label={i18n.t("contactModal.form.email")}
										name="email"
										error={touched.email && Boolean(errors.email)}
										helperText={touched.email && errors.email}
										placeholder="Email address"
										fullWidth
										margin="dense"
										variant="outlined"
									/>
								</div>
								<div>
  <Field
    as={TextField}
    label="Data de Nascimento"
    name="birthDate"
    type="date"
    InputLabelProps={{
      shrink: true,
    }}
    fullWidth
    margin="dense"
    variant="outlined"
    helperText="Data de nascimento para mensagens automáticas de aniversário"
    onChange={(e) => {
      const formattedDate = parseDateFromInput(e.target.value);
      setFieldValue('birthDate', formattedDate);
    }}
  />
</div>

								<div>
									<TagsContainer 
										key={contactId || 'new-contact'} 
										contact={
											contactId 
												? contact 
												: { 
													...contact, 
													tags: selectedTags,
													onTagsChange: (tags) => setSelectedTags(tags)
												}
										} 
										className={classes.textField} 
									/>
								</div>

								<Grid item>
									<Typography
										variant="subtitle1"
										gutterBottom
										style={{ marginTop: 24, marginBottom: 8 }}
									>
										<b>{i18n.t("contactModal.form.assignWallet")}</b>
									</Typography>
								</Grid>
								{/* <div className={classes.blockCustomerField}> */}
								<Grid container spacing={2}>
									<Grid xs={12} sm={6} xl={6} item>
										<Autocomplete
											value={selectedUser}
											fullWidth
											disablePortal={false}
											getOptionLabel={(option) => option?.name || ""}
											isOptionEqualToValue={(option, value) => {
												if (!option || !value) return false;
												return option.id === value.id;
											}}
											onInputChange={(event, newInputValue, reason) => {
												// Atualizar searchParam sempre que o input mudar
												console.log("[ContactModal] onInputChange:", { newInputValue, reason });
												setSearchParam(newInputValue || "");
												// Se o campo foi limpo, limpar também o usuário selecionado
												if (reason === 'clear' || !newInputValue || newInputValue.trim() === "") {
													if (reason === 'clear') {
														setSelectedUser(null);
														setQueues(allQueues);
														setSelectedQueue(null);
														setAutocompleteOpen(false);
													}
												} else if (reason === 'input' && newInputValue.length >= 3) {
													// Abrir dropdown quando o usuário está digitando (após buscar)
													// O useEffect vai buscar e atualizar options, então vamos abrir depois
												}
											}}
											open={autocompleteOpen && options.length > 0 && !loading}
											onOpen={() => {
												console.log("[ContactModal] Autocomplete onOpen - options:", options.length);
												if (options.length > 0) {
													setAutocompleteOpen(true);
												}
											}}
											onClose={(event, reason) => {
												console.log("[ContactModal] Autocomplete onClose - reason:", reason);
												setAutocompleteOpen(false);
											}}
											onChange={async (e, newValue) => {
												console.log("[ContactModal] Usuário selecionado:", newValue);
												setSelectedUser(newValue);
												setAutocompleteOpen(false);
												if (newValue) {
													setSearchParam(newValue.name || "");
													console.log("[ContactModal] Filas do usuário no objeto:", newValue.queues);
													
													// Verificar se o usuário tem filas no objeto retornado
													if (Array.isArray(newValue.queues) && newValue.queues.length > 0) {
														if (newValue.queues.length === 1) {
															setSelectedQueue(newValue.queues[0].id);
														} else {
															// Se havia uma fila selecionada que não está nas filas do novo usuário, limpar
															if (selectedQueue && !newValue.queues.some(q => q.id === selectedQueue)) {
																setSelectedQueue(null);
															}
														}
														setQueues(newValue.queues);
														console.log("[ContactModal] Filas atualizadas do objeto:", newValue.queues.length);
													} else {
														// Se não tem filas no objeto, buscar do servidor
														try {
															const { data: userData } = await api.get(`/users/${newValue.id}`);
															console.log("[ContactModal] Dados completos do usuário:", userData);
															if (userData && Array.isArray(userData.queues) && userData.queues.length > 0) {
																if (isMounted.current) {
																	setQueues(userData.queues);
																	if (userData.queues.length === 1) {
																		setSelectedQueue(userData.queues[0].id);
																	}
																	console.log("[ContactModal] Filas buscadas do servidor:", userData.queues.length);
																}
															} else {
																// Se não tem filas, mostrar todas
																if (isMounted.current) {
																	setQueues(allQueues);
																}
															}
														} catch (err) {
															console.error("[ContactModal] Erro ao buscar filas:", err);
															// Em caso de erro, mostrar todas as filas
															if (isMounted.current) {
																setQueues(allQueues);
															}
														}
													}
												} else {
													// Se não há usuário selecionado, mostrar todas as filas
													setQueues(allQueues);
													setSelectedQueue(null);
													setSearchParam("");
												}
											}}
											options={options}
											filterOptions={(options, state) => {
												// Não filtrar localmente, já que estamos buscando no servidor
												console.log("[ContactModal] filterOptions - options recebidas:", options.length, "inputValue:", state.inputValue);
												return options;
											}}
											freeSolo
											autoHighlight
											ListboxProps={{
												style: { maxHeight: '300px' },
												onMouseDown: (e) => {
													// Prevenir que o dropdown feche ao clicar nele
													e.preventDefault();
												}
											}}
											componentsProps={{
												popper: {
													style: {
														zIndex: 13000
													},
													placement: 'bottom-start',
													disablePortal: false
												},
												paper: {
													style: {
														zIndex: 13000,
														textAlign: 'left',
														maxHeight: 300,
														overflow: 'auto'
													}
												}
											}}
											noOptionsText={
												searchParam.length < 3 
													? "Digite pelo menos 3 caracteres para buscar" 
													: loading 
														? "Buscando..." 
														: i18n.t("transferTicketModal.noOptions")
											}
											loading={loading}
											renderOption={(props, option) => {
												console.log("[ContactModal] renderOption chamado para:", option.name);
												return (
													<li {...props} key={option.id}>
														<span style={{ marginLeft: 8 }}>{option.name}</span>
													</li>
												);
											}}
											renderInput={(params) => {
												return (
													<TextField
														{...params}
														label={i18n.t("transferTicketModal.fieldLabel")}
														variant="outlined"
														placeholder="Digite para buscar usuários"
														InputProps={{
															...params.InputProps,
															endAdornment: (
																<React.Fragment>
																	{loading ? (
																		<CircularProgress color="inherit" size={20} />
																	) : null}
																	{params.InputProps.endAdornment}
																</React.Fragment>
															),
														}}
													/>
												);
											}}
										/>
									</Grid>

									<Grid xs={12} sm={6} xl={6} item>
										<FormControl variant="outlined" fullWidth>
											<InputLabel>
												{i18n.t("transferTicketModal.fieldQueueLabel")}
											</InputLabel>
											<Select
												value={selectedQueue || ""}
												onChange={(e) => setSelectedQueue(e.target.value)}
												label={i18n.t("transferTicketModal.fieldQueueLabel")}
												MenuProps={{
													style: {
														zIndex: 10004
													},
													PaperProps: {
														style: {
															zIndex: 10004
														}
													},
													disablePortal: false
												}}
											>
												{queues.length > 0 ? (
													queues.map((queue) => (
														<MenuItem key={queue.id} value={queue.id}>
															{queue.name}
														</MenuItem>
													))
												) : (
													<MenuItem disabled>
														{selectedUser 
															? "Usuário não possui filas associadas"
															: "Selecione um usuário primeiro"}
													</MenuItem>
												)}
											</Select>
										</FormControl>
									</Grid>
								</Grid>

								<Typography
									style={{ marginBottom: 8, marginTop: 12 }}
									variant="subtitle1"
								>
									<Switch
										size="small"
										checked={disableBot}
										onChange={() =>
											setDisableBot(!disableBot)
										}
										name="disableBot"
									/>
									{i18n.t("contactModal.form.chatBotContact")}
								</Typography>
								<Typography
									style={{ marginBottom: 8, marginTop: 12 }}
									variant="subtitle1"
								>
									{i18n.t("contactModal.form.whatsapp")} {contact?.whatsapp ? contact?.whatsapp.name : ""}
								</Typography>
								<Typography
									style={{ marginBottom: 8, marginTop: 12 }}
									variant="subtitle1"
								>
									{i18n.t("contactModal.form.termsLGDP")} {contact?.lgpdAcceptedAt ? format(new Date(contact?.lgpdAcceptedAt), "dd/MM/yyyy 'às' HH:mm") : ""}
								</Typography>
								
								{/* Campo Floup - Status e Etapa Atual */}
								<div style={{ marginTop: 12, marginBottom: 8 }}>
									<Typography variant="subtitle1" gutterBottom>
										<b>Floup</b>
									</Typography>
									{loadingFloup ? (
										<CircularProgress size={20} />
									) : activeFloup ? (
										<TextField
											fullWidth
											disabled
											variant="outlined"
											margin="dense"
											value={`${activeFloup.name || 'Floup'} - Etapa ${(activeFloup.activeSchedule?.currentStepIndex || 0) + 1} de ${(activeFloup.steps || []).length}`}
											helperText={
												activeFloup.activeSchedule?.nextRunAt 
													? `Próxima execução: ${format(new Date(activeFloup.activeSchedule.nextRunAt), "dd/MM/yyyy 'às' HH:mm")}`
													: "Floup ativo"
											}
											InputProps={{
												style: {
													borderColor: '#ff9800',
													borderWidth: '2px',
													borderStyle: 'solid'
												}
											}}
										/>
									) : (
										<TextField
											fullWidth
											disabled
											variant="outlined"
											margin="dense"
											value="Nenhum Floup ativo"
											helperText="Este contato não está em nenhum Floup no momento"
											InputProps={{
												style: {
													borderColor: '#ccc',
													borderWidth: '1px',
													borderStyle: 'solid'
												}
											}}
										/>
									)}
								</div>

								{/* <Typography variant="subtitle1" gutterBottom>{i18n.t("contactModal.form.customer_portfolio")}</Typography> */}
								{/* <div style={{ marginTop: 10 }}>
									<AsyncSelect url="/users" dictKey={"users"}
										initialValue={values.user} width="100%" label={i18n.t("contactModal.form.attendant")}
										onChange={(event, value) => setFieldValue("userId", value ? value.id : null)} />
								</div>
								<div style={{ marginTop: 10 }}>
									<AsyncSelect url="/queue" dictKey={null}
										initialValue={values.queue} width="100%" label={i18n.t("contactModal.form.queue")}
										onChange={(event, value) => setFieldValue("queueId", value ? value.id : null)} />
								</div> */}
								<Typography
									style={{ marginBottom: 8, marginTop: 12 }}
									variant="subtitle1"
								>
									{i18n.t("contactModal.form.extraInfo")}
								</Typography>

								<FieldArray name="extraInfo">
									{({ push, remove }) => (
										<>
											{values.extraInfo &&
												values.extraInfo.length > 0 &&
												values.extraInfo.map((info, index) => (
													<div
														className={classes.extraAttr}
														key={`${index}-info`}
													>
														<Field
															as={TextField}
															label={i18n.t("contactModal.form.extraName")}
															name={`extraInfo[${index}].name`}
															variant="outlined"
															margin="dense"
															className={classes.textField}
														/>
														<Field
															as={TextField}
															label={i18n.t("contactModal.form.extraValue")}
															name={`extraInfo[${index}].value`}
															variant="outlined"
															margin="dense"
															className={classes.textField}
														/>
														<IconButton
															size="small"
															onClick={() => remove(index)}
														>
															<DeleteOutlineIcon />
														</IconButton>
													</div>
												))}
											<div className={classes.extraAttr}>
												<Button
													style={{ flex: 1, marginTop: 8 }}
													variant="outlined"
													color="primary"
													onClick={() => push({ name: "", value: "" })}
												>
													{`+ ${i18n.t("contactModal.buttons.addExtraInfo")}`}
												</Button>
											</div>
										</>
									)}
								</FieldArray>
									</Box>
								)}

								{tabValue === 1 && (
									<Box>
										<Grid container spacing={2}>
											<Grid item xs={12} sm={6}>
												<ContactFieldAutocomplete
													name="situacao"
													label={i18n.t("contactModal.form.situacao")}
													options={situacaoOptions.length ? situacaoOptions : SITUACAO_FALLBACK}
													disabled={loadingCustomAttributes}
												/>
											</Grid>

											<Grid item xs={12} sm={6}>
												<ContactFieldAutocomplete
													name="canal"
													label={i18n.t("contactModal.form.canal")}
													options={canalOptions.length ? canalOptions : CANAL_FALLBACK}
													disabled={loadingCustomAttributes}
												/>
											</Grid>

											<Grid item xs={12} sm={6}>
												<ContactFieldAutocomplete
													name="regiao"
													label={i18n.t("contactModal.form.regiao")}
													options={regiaoOptions.length ? regiaoOptions : REGIAO_FALLBACK}
													disabled={loadingCustomAttributes}
												/>
											</Grid>

											{(contactFieldGroups || [])
												.filter(g => !["SITUACAO", "CANAL", "REGIAO"].includes(g.param))
												.filter(g =>
													["SELECT_SINGLE", "SELECT_MULTI", "SELECT_MULTI_LIMITED"].includes(g.fieldKind)
												)
												.sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0) || a.id - b.id)
												.map(g => {
													const opts = (customAttributeRows || [])
														.filter(r => r.param === g.param)
														.map(r => r.value);
													const fname = `customDynamicFields.${g.param}`;
													if (g.fieldKind === "SELECT_SINGLE") {
														return (
															<Grid item xs={12} sm={6} key={g.id}>
																<ContactFieldAutocomplete
																	name={fname}
																	label={g.label}
																	options={opts}
																	disabled={loadingCustomAttributes}
																/>
															</Grid>
														);
													}
													if (g.fieldKind === "SELECT_MULTI" || g.fieldKind === "SELECT_MULTI_LIMITED") {
														return (
															<Grid item xs={12} sm={6} key={g.id}>
																<ContactFieldAutocomplete
																	name={fname}
																	label={g.label}
																	options={opts}
																	multiple
																	maxSelections={
																		g.fieldKind === "SELECT_MULTI_LIMITED"
																			? Number(g.multiMax) || 0
																			: 0
																	}
																	disabled={loadingCustomAttributes}
																/>
															</Grid>
														);
													}
													return null;
												})}
										</Grid>
									</Box>
								)}
							</DialogContent>
							<DialogActions>
								<Button
									onClick={handleClose}
									color="secondary"
									disabled={isSubmitting}
									variant="outlined"
								>
									{i18n.t("contactModal.buttons.cancel")}
								</Button>
								<Button
									type="submit"
									color="primary"
									disabled={isSubmitting}
									variant="contained"
									className={classes.btnWrapper}
								>
									{contactId
										? `${i18n.t("contactModal.buttons.okEdit")}`
										: `${i18n.t("contactModal.buttons.okAdd")}`}
									{isSubmitting && (
										<CircularProgress
											size={24}
											className={classes.buttonProgress}
										/>
									)}
								</Button>
							</DialogActions>
						</Form>
					)}
				</Formik>
			</Dialog>
		</div>
	);
};

export default ContactModal;
